import { Component, ElementRef, HostListener, Inject, Injector, Input, OnInit, PLATFORM_ID, ViewChild, ViewEncapsulation } from '@angular/core';
import { UserLandingsModuleModel, UserLandingItem, UserLandingTabTextData } from './user-landings-models';
import { AbstractComponent } from '../abstract.component';
import { ModalData, ModalService } from '@shopping-angular-components/modal';
import { ConfirmButtonsComponent } from '../confirm-buttons/confirm-buttons.component';
import { HttpClient } from '@angular/common/http';
import { UpaTarget } from 'projects/common/services/upa.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { isPlatformBrowser } from '@angular/common';
import { LandingModel } from 'projects/common/model/landingModel';

interface TabSelected {
  name: string;
  index: number;
}

@Component({
  selector: 'user-landings',
  template: `
    <div #plannersContainer>
      <eva-tabs [product]="'nymeria-gui'" [flow]="flow" (tabSelected)="onTabSelected($event)">
        <ng-container *ngFor="let tripList of module.data?.all_trips; let i = index">

          <eva-tab [title]="tabsTextData[i].title" [active]="i === currentTabIndex">
            <ng-container *ngIf="hasItems(tripList); else emptyStateTab">
              <user-landing-card @fade *ngFor="let trip of tripList" [userLandingCard]="trip"
                (deleteEvent)="showDeleteConfirmationModal(trip)" [upaData]="upaData"
                [moduleIndex]="moduleIndex" [moduleId]="moduleId" [uniqueId]="uniqueId"
                [withDeleteButton]="i !== 0"
                (@fade.done)="onCardAnimationDone($event)">
              </user-landing-card>
            </ng-container>

            <ng-template #emptyStateTab>
              <ng-container *ngIf="showEmptyState">
                <h4 class="eva-3-h4 -eva-3-mb-sm">{{tabsTextData[i].emptyStateTitle}}</h4>
                <p class="eva-3-body-2">{{tabsTextData[i].emptyStateText}}</p>
                <eva-button class="-eva-3-mt-md"
                  [options]="{type: '-primary', link: tabsTextData[i].emptyStateButtonUrl, link_text: tabsTextData[i].emptyStateButtonText}"
                  [moduleId]="getModuleId()"
                  [upaData]="{label: 'EMPTY_TRIPPLANNER_BUTTON'}">
                </eva-button>
              </ng-container>
            </ng-template>
          </eva-tab>

        </ng-container>
      </eva-tabs>
    </div>

    <div *ngIf="hasItems()" class="new-trip-button-container" [ngClass]="{'-fixed': fixedOnBottom}">
      <eva-button class="new-trip-button -eva-3-mt-md"
      [options]="{type: '-primary', link_text: ('userLandings.newTrip' | translate), icon: 'plus'}"
      [moduleId]="getModuleId()"
      [upaData]="{label: 'NEW_TRIPPLANNER_BUTTON'}"
      (clickEvent)="goToNewUserLanding()"></eva-button>
    </div>

    <eva-message @fade *ngIf="message.show && message.type === 'success'"
      [data]="{message_type: '-success', icon:'checkmark-circle-filled', text: 'userLandings.landingDeletedText', title: 'userLandings.landingDeletedTitle', text_params: message.params}"></eva-message>
    <eva-message @fade *ngIf="message.show && message.type === 'error'"
      [data]="{message_type: '-error', icon: 'error-circle', text: 'userLandings.landingErrorText', title: 'userLandings.landingErrorTitle', text_params: message.params}"></eva-message>

  `,
  animations: [
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('500ms', style({ opacity: 0 }))
      ])
    ]),
  ],
  styleUrls: ['./user-landings.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UserLandingsComponent extends AbstractComponent implements OnInit {

  @Input() moduleId: string;
  @Input() uniqueId: string;
  @Input() override module: UserLandingsModuleModel;
  @Input() moduleIndex: number;

  @ViewChild('plannersContainer') plannersContainer: ElementRef<HTMLInputElement>;

  tabsTextData: UserLandingTabTextData[];
  currentTabIndex: number = 0;
  showEmptyState: boolean;
  fixedOnBottom = false;
  landingModel: LandingModel;
  upaData: any;
  message: {
    show: boolean,
    type: 'success'|'error',
    params:{
      'cityName':string,
    }
  } = { show: false, type: 'success', params: {cityName: ''} };

  constructor(
    injector: Injector,
    private modalService: ModalService,
    private httpClient: HttpClient,
    private translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: string) {
      super(injector);
    }

  @HostListener('window:scroll') onScrollEvent() {
    this.fixedOnBottom = (this.plannersContainer.nativeElement.getBoundingClientRect().bottom + 64) > window.innerHeight ;
  }

  ngOnInit(): void {
    this.landingModel = this.landingModelService.getLandingModel();
    if (isPlatformBrowser(this.platformId)) {
      this.tabsTextData = this.translateService.instant("userLandings.tabs");
      const partnerGroup = this.landingModelService.getLandingModel().partner?.group;
      if (["falabella","bestday","viajanet"].includes(partnerGroup)){
        this.tabsTextData[0].emptyStateButtonUrl = this.translateService.instant(`userLandings.hub.${partnerGroup}`);
      }
      this.showEmptyState = !this.hasItems();
      this.upaData = {label: '', uniqueId: this.uniqueId, moduleId: this.moduleId, moduleIndex: this.moduleIndex, target: UpaTarget.TRIP_PLANNER};
      this.currentTabIndex = this.module.data.default_selected_tab_index;
    }
  }

  getModuleId(): string {
    return this.moduleId
  }

  getModuleData(): string {
    return JSON.stringify(this.module.data);
  }

  onCardAnimationDone(event: any) {
    this.showEmptyState = !this.hasItems();
  }

  hasItems(trips?: UserLandingItem[]): boolean {
    const currentTabTrips = trips ? trips : this.module.data?.all_trips[this.currentTabIndex];
    return currentTabTrips.length !== 0 && currentTabTrips.some(trip => !trip.deleted);
  }

  onTabSelected(selectedTab: TabSelected): void {
    this.currentTabIndex = selectedTab.index;
    this.showEmptyState = !this.hasItems();
  }

  deleteUserLanding(item: UserLandingItem) {
    let url = '/nymeria-gui/data/model/removeUserLanding/'+ item.id;

    return new Promise((resolve, reject) => {
      this.httpClient.delete(url)
          .subscribe(
            (response: any) => {
              resolve(response);
              item.deleted = true; // para la animación
              this.showMessage(item.city_name, 'success');
              this.trackUpa('DELETION_SUCCESS','');
            },
            (err: any) => {
              reject(err.error);
              this.showMessage(item.city_name, 'error');
              this.trackUpa('DELETION_ERROR','');
            }
          );
      });
  }

  showDeleteConfirmationModal(item: UserLandingItem) {
    const modalData: ModalData = {
      id: 'delete-user-landing-modal',
      title: this.translateService.instant('userLandings.confirmDeletionTitle'),
      content: this.translateService.instant('userLandings.confirmDeletionText', {cityName: item.city_name}),
      size: '-sm',
      footerComponent: ConfirmButtonsComponent,
      footerModel: {
        acceptCallback: () => {
          this.deleteUserLanding(item).finally(() =>
            this.modalService.closeModal()
          );
        },
        cancelCallback: () => {
          this.modalService.closeModal();
        },
        acceptLabel: this.translateService.instant('userLandings.deleteButton'),
        cancelLabel: this.translateService.instant('userLandings.cancelButton'),
        loaderLabel: this.translateService.instant('userLandings.buttonLoader'),
        cancelButtonType: '-link',
        moduleId: this.moduleId,
        moduleIndex: this.moduleIndex,
        uniqueId: this.uniqueId,
        size: globalThis.innerWidth <= 340? '-md' : '-lg'
      },
      isWebview: this.landingModel.webview,
      product: 'nymeria-gui',
      flow: 'landing',
      forceHeader: true
    };

    this.modalService.showModal(modalData);
  }

  showMessage(cityName: string, messageType: 'success' | 'error'){
    this.message.type = messageType;
    this.message.params.cityName = cityName;
    this.message.show = true;
    setTimeout(() => {
      this.message.show = false;
      this.message.params.cityName = '';
    }, 5000);
  }

  goToNewUserLanding() {
    window.location.href = this.translateService.instant('userLandings.formUrl');
  }

  trackUpa(label:string, url: string){
    let dataToTrack = {...this.upaData, label: label};
    dataToTrack.url = url || dataToTrack.url;

    this.upaService.trackClickTopic(dataToTrack);
  }
}
